"use client";

import type { ReactNode } from "react";
import { useId } from "react";
import Select, { type SingleValue, type StylesConfig } from "react-select";
import clsx from "clsx";

type Option = {
  value: string;
  label: string;
  isDisabled?: boolean;
};

type Props = {
  label: ReactNode;
  options: Option[];
  value?: string;
  onChange: (val: string) => void;
  error?: string;
  placeholder?: string;
  isDisabled?: boolean;
  isLoading?: boolean;
  isSearchable?: boolean;
  variant?: "default" | "simple" | "compact";
  /**
   * Keep the menu open after a selection. Defaults to react-select's behavior
   * (close on select). Used by drill-in selects (e.g. the Free Trial category
   * "Other" group) that swap their option list in place and must stay open so
   * the user sees the revealed sub-options.
   */
  closeMenuOnSelect?: boolean;
  /** Controlled menu-open state. Omit to leave the menu uncontrolled. */
  menuIsOpen?: boolean;
  onMenuOpen?: () => void;
  onMenuClose?: () => void;
  /**
   * A stable id for react-select's internal `instanceId` (used to build its
   * `react-select-{id}-...` DOM ids, e.g. `-live-region`, `-placeholder`).
   * Without it, react-select falls back to a module-level counter
   * (`++instanceId` in its Select component) that increments once per
   * <Select> constructed during a JS execution context's lifetime - the
   * server and client are separate contexts that don't necessarily
   * construct the same number of Selects before this one, so the same
   * component can land on a different counter value in each, causing a
   * hydration mismatch on every id react-select derives from it. Defaults
   * to a React `useId()` value, which is generated from this component
   * instance's actual position in the tree and is guaranteed identical
   * between the server render and the client hydration render, and unique
   * against every other FormSelect on the page - so no call site needs to
   * pass one explicitly unless it wants a specific, human-readable id.
   */
  instanceId?: string;
};

export default function FormSelect({
  label,
  options,
  value,
  onChange,
  error,
  placeholder,
  isDisabled,
  isLoading,
  isSearchable = true,
  variant = "default",
  instanceId,
  closeMenuOnSelect,
  menuIsOpen,
  onMenuOpen,
  onMenuClose,
}: Props) {
  const generatedInstanceId = useId();

  return (
    <div>
      {label ? (
        <label className="block text-foreground text-sm mb-1.5">
          {label}
        </label>
      ) : null}

      <Select
        instanceId={instanceId ?? generatedInstanceId}
        options={options}
        placeholder={placeholder}
        value={options.find((opt) => opt.value === value) ?? null}
        onChange={(selected: SingleValue<Option>) => onChange(selected?.value ?? "")}
        className={clsx(error && "border-red-500")}
        isDisabled={isDisabled}
        isLoading={isLoading}
        isSearchable={isSearchable}
        closeMenuOnSelect={closeMenuOnSelect}
        menuIsOpen={menuIsOpen}
        onMenuOpen={onMenuOpen}
        onMenuClose={onMenuClose}
        styles={
          variant === "compact"
            ? compactSelectStyles
            : variant === "simple"
              ? simpleSelectStyles
              : defaultSelectStyles
        }
      />

      {error && (
        <p className="text-red-500 text-xs mt-1">{error}</p>
      )}
    </div>
  );
}

const defaultSelectStyles: StylesConfig<Option, false> = {
  control: (base, state) => ({
    ...base,
    minHeight: 44,
    borderRadius: 8,
    fontSize: 14,
    borderColor: state.isFocused ? "var(--focus-ring)" : "var(--border)",
    boxShadow: state.isFocused
      ? "0 0 0 2px color-mix(in srgb, var(--focus-ring) 20%, transparent)"
      : "none",
    backgroundColor: state.isDisabled ? "var(--hover)" : "var(--input)",
    transition: "background-color 150ms ease, border-color 150ms ease",
    cursor: state.isDisabled ? "not-allowed" : "default",
    opacity: state.isDisabled ? 0.8 : 1,
    "&:hover": {
      borderColor: state.isFocused ? "var(--focus-ring)" : "var(--text-muted)",
    },
  }),
  valueContainer: (base) => ({
    ...base,
    paddingInline: 12,
  }),
  singleValue: (base) => ({
    ...base,
    color: "var(--foreground)",
    fontSize: 14,
  }),
  placeholder: (base) => ({
    ...base,
    color: "var(--text-muted)",
    fontSize: 14,
  }),
  menu: (base) => ({
    ...base,
    borderRadius: 10,
    overflow: "hidden",
    border: "1px solid var(--border)",
    boxShadow: "0 10px 30px rgba(15, 23, 42, 0.08)",
    backgroundColor: "var(--surface-elevated)",
    zIndex: 9999,
  }),
  menuList: (base) => ({
    ...base,
    backgroundColor: "var(--surface-elevated)",
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected ? "var(--selected)" : state.isFocused ? "var(--hover)" : "var(--surface-elevated)",
    color: state.isDisabled ? "var(--text-muted)" : "var(--foreground)",
    cursor: state.isDisabled ? "not-allowed" : "pointer",
    fontSize: 14,
    fontWeight: state.isSelected ? 600 : 500,
    opacity: state.isDisabled ? 0.6 : 1,
  }),
};

const compactSelectStyles: StylesConfig<Option, false> = {
  control: (base, state) => ({
    ...base,
    minHeight: 38,
    borderRadius: 10,
    borderColor: state.isFocused ? "var(--focus-ring)" : "var(--border)",
    boxShadow: state.isFocused ? "0 0 0 2px color-mix(in srgb, var(--focus-ring) 20%, transparent)" : "none",
    backgroundColor: state.isDisabled ? "var(--hover)" : "var(--input)",
    transition: "background-color 150ms ease, border-color 150ms ease",
    paddingInline: 2,
    cursor: state.isDisabled ? "not-allowed" : "default",
    opacity: state.isDisabled ? 0.8 : 1,
    "&:hover": {
      borderColor: state.isFocused ? "var(--focus-ring)" : "var(--text-muted)",
    },
  }),
  valueContainer: (base) => ({
    ...base,
    paddingBlock: 0,
    paddingInline: 8,
  }),
  input: (base) => ({
    ...base,
    margin: 0,
    paddingBlock: 0,
    color: "var(--foreground)",
  }),
  placeholder: (base) => ({
    ...base,
    color: "var(--text-muted)",
    fontSize: 13,
  }),
  singleValue: (base, state) => ({
    ...base,
    color: state.isDisabled ? "var(--text-muted)" : "var(--foreground)",
    fontSize: 13,
    fontWeight: 500,
  }),
  indicatorSeparator: () => ({
    display: "none",
  }),
  dropdownIndicator: (base) => ({
    ...base,
    color: "var(--text-muted)",
    paddingInline: 6,
    paddingBlock: 4,
  }),
  clearIndicator: (base) => ({
    ...base,
    paddingInline: 6,
    paddingBlock: 4,
  }),
  menu: (base) => ({
    ...base,
    borderRadius: 10,
    overflow: "hidden",
    border: "1px solid var(--border)",
    boxShadow: "0 10px 30px rgba(15, 23, 42, 0.08)",
    backgroundColor: "var(--surface-elevated)",
    zIndex: 9999,
  }),
  menuList: (base) => ({
    ...base,
    backgroundColor: "var(--surface-elevated)",
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected ? "var(--selected)" : state.isFocused ? "var(--hover)" : "var(--surface-elevated)",
    color: state.isDisabled ? "var(--text-muted)" : "var(--foreground)",
    cursor: state.isDisabled ? "not-allowed" : "pointer",
    fontSize: 13,
    fontWeight: state.isSelected ? 600 : 500,
    opacity: state.isDisabled ? 0.6 : 1,
  }),
};

const simpleSelectStyles: StylesConfig<Option, false> = {
  control: (base, state) => ({
    ...base,
    minHeight: 46,
    borderRadius: 10,
    borderColor: state.isFocused ? "var(--focus-ring)" : "var(--border)",
    boxShadow: state.isFocused ? "0 0 0 2px color-mix(in srgb, var(--focus-ring) 20%, transparent)" : "none",
    backgroundColor: state.isDisabled ? "var(--hover)" : "var(--input)",
    transition: "background-color 150ms ease, border-color 150ms ease",
    paddingInline: 4,
    cursor: state.isDisabled ? "not-allowed" : "default",
    opacity: state.isDisabled ? 0.8 : 1,
    "&:hover": {
      borderColor: state.isFocused ? "var(--focus-ring)" : "var(--text-muted)",
    },
  }),
  valueContainer: (base) => ({
    ...base,
    paddingBlock: 0,
    paddingInline: 8,
  }),
  placeholder: (base) => ({
    ...base,
    color: "var(--text-muted)",
    fontSize: 14,
  }),
  singleValue: (base, state) => ({
    ...base,
    color: state.isDisabled ? "var(--text-muted)" : "var(--foreground)",
    fontSize: 14,
    fontWeight: 500,
  }),
  indicatorSeparator: () => ({
    display: "none",
  }),
  dropdownIndicator: (base) => ({
    ...base,
    color: "var(--text-muted)",
    paddingInline: 8,
  }),
  menu: (base) => ({
    ...base,
    borderRadius: 10,
    overflow: "hidden",
    border: "1px solid var(--border)",
    boxShadow: "0 10px 30px rgba(15, 23, 42, 0.08)",
    backgroundColor: "var(--surface-elevated)",
    zIndex: 9999,
  }),
  menuList: (base) => ({
    ...base,
    backgroundColor: "var(--surface-elevated)",
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected ? "var(--selected)" : state.isFocused ? "var(--hover)" : "var(--surface-elevated)",
    color: state.isDisabled ? "var(--text-muted)" : "var(--foreground)",
    cursor: state.isDisabled ? "not-allowed" : "pointer",
    fontSize: 14,
    fontWeight: state.isSelected ? 600 : 500,
    opacity: state.isDisabled ? 0.6 : 1,
  }),
};
